import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Package, Truck, Shirt, CalendarPlus, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { addDays, format, isBefore, startOfDay } from "date-fns";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

const CAL_MODIFIER_STYLES = {
  rangeFull: { background: "rgba(255,79,0,0.12)", color: "#0A0A0A", borderRadius: 0 },
};

const CAL_CLASSNAMES = {
  months: "flex flex-col md:flex-row gap-8",
  month: "space-y-3",
  caption: "flex justify-center pt-1 relative items-center font-display font-bold text-base",
  nav_button: "h-7 w-7 inline-flex items-center justify-center hover:bg-neutral-100",
  nav_button_previous: "absolute left-1",
  nav_button_next: "absolute right-1",
  head_cell: "w-9 text-[10px] uppercase tracking-widest text-neutral-400 font-semibold",
  cell: "p-0",
  day: "h-9 w-9 inline-flex items-center justify-center text-sm hover:bg-neutral-100 transition-colors",
  day_selected: "bg-[#FF4F00] text-white hover:bg-[#FF4F00] font-bold",
  day_today: "ring-1 ring-[#FF4F00]",
  day_disabled: "text-neutral-300 hover:bg-transparent cursor-not-allowed",
  day_outside: "text-neutral-300",
};

export default function Booking() {
  const { tierId } = useParams();
  const navigate = useNavigate();
  const [tier, setTier] = useState(null);
  const [startDate, setStartDate] = useState();
  const [step, setStep] = useState(1); // 1 calendar, 2 form
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState(null); // { remaining, available } | null
  const [damageProtection, setDamageProtection] = useState(true); // opt-out, on by default
  const [addonCatalog, setAddonCatalog] = useState([]);
  const [addonQty, setAddonQty] = useState({}); // { addon_id: number }
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    delivery_address: "",
    notes: "",
  });

  useEffect(() => {
    api.get(`/addons`)
      .then((r) => setAddonCatalog(r.data.filter((a) => a.id !== "damage_protection")))
      .catch(() => setAddonCatalog([]));
  }, []);

  useEffect(() => {
    api
      .get(`/tiers/${tierId}`)
      .then((r) => setTier(r.data))
      .catch(() => {
        toast.error("Tier not found");
        navigate("/");
      });
  }, [tierId, navigate]);

  const endDate = useMemo(
    () => (startDate ? addDays(startDate, 13) : null),
    [startDate]
  );

  // Check availability whenever start/end changes
  useEffect(() => {
    if (!startDate || !endDate || !tier) {
      setAvailability(null);
      return;
    }
    let cancelled = false;
    api
      .get(`/availability`, {
        params: {
          tier_id: tier.id,
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date: format(endDate, "yyyy-MM-dd"),
        },
      })
      .then((r) => { if (!cancelled) setAvailability(r.data); })
      .catch(() => { if (!cancelled) setAvailability(null); });
    return () => { cancelled = true; };
  }, [startDate, endDate, tier]);

  const range = useMemo(() => {
    if (!startDate || !endDate) return undefined;
    return { from: startDate, to: endDate };
  }, [startDate, endDate]);

  const calendarModifiers = useMemo(
    () => ({ rangeFull: range ? { from: range.from, to: range.to } : undefined }),
    [range]
  );

  const minDate = startOfDay(addDays(new Date(), 2));

  const selectedAddons = useMemo(
    () => addonCatalog
      .filter((a) => (addonQty[a.id] || 0) > 0)
      .map((a) => ({ addon: a, quantity: addonQty[a.id] })),
    [addonCatalog, addonQty]
  );
  const addonsSubtotal = selectedAddons.reduce(
    (s, { addon, quantity }) => s + addon.price * quantity, 0
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error("Please pick a start date first.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/create", {
        tier_id: tier.id,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        ...form,
        damage_protection: damageProtection,
        addons: selectedAddons.map(({ addon, quantity }) => ({ addon_id: addon.id, quantity })),
        origin_url: window.location.origin,
      });
      // Redirect to Stripe hosted checkout
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  if (!tier) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF4F00]" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F8F8] min-h-screen">
      <Nav />
      <section className="pt-24 md:pt-28 pb-24" data-testid="booking-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-[#FF4F00] mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to tiers
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* LEFT — summary */}
            <aside className="lg:col-span-4">
              <div className="bg-white border border-neutral-200 p-8 md:p-10 sticky top-24">
                <span className="text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                  Your selection
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-2" data-testid="summary-tier-name">
                  {tier.name}
                </h2>
                <p className="text-neutral-600 text-sm mt-1">{tier.tagline}</p>

                <div className="mt-8 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-bold" data-testid="summary-price">${tier.price}</span>
                  <span className="text-sm text-neutral-500">/ 2 weeks</span>
                </div>

                <div className="mt-8 space-y-3 border-t border-neutral-200 pt-6">
                  <Row label="Totes" value={`${tier.tote_count}`} />
                  <Row label="Window" value={`${tier.duration_days} days`} />
                  <Row
                    label="Start"
                    value={startDate ? format(startDate, "MMM d, yyyy") : "—"}
                    testid="summary-start"
                  />
                  <Row
                    label="End"
                    value={endDate ? format(endDate, "MMM d, yyyy") : "—"}
                    testid="summary-end"
                  />
                </div>

                <div className="mt-6 border-t border-neutral-200 pt-6 space-y-3 bg-[#FF4F00]/5 -mx-8 -mb-8 md:-mx-10 md:-mb-10 px-8 md:px-10 pb-8 md:pb-10">
                  <Row label="Deposit today" value={`$${tier.deposit}`} testid="summary-deposit" />
                  {damageProtection && (
                    <Row label="Damage protection" value="$19" testid="summary-damage-protection" />
                  )}
                  {addonsSubtotal > 0 && (
                    <Row label={`Add-ons (${selectedAddons.reduce((s, x) => s + x.quantity, 0)})`} value={`$${addonsSubtotal}`} testid="summary-addons" />
                  )}
                  <Row
                    label="Charged today"
                    value={`$${tier.deposit + (damageProtection ? 19 : 0) + addonsSubtotal}`}
                    testid="summary-charged-today"
                  />
                  <Row label="Balance on delivery" value={`$${tier.price - tier.deposit}`} testid="summary-balance" />
                  <div className="pt-3 border-t border-neutral-300 mt-3 flex justify-between">
                    <span className="text-sm font-semibold text-[#0A0A0A]">Total</span>
                    <span className="font-display text-xl font-bold text-[#0A0A0A]" data-testid="summary-total">
                      ${tier.price + (damageProtection ? 19 : 0) + addonsSubtotal}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500 pt-2 leading-relaxed">
                    We charge <span className="font-semibold text-[#0A0A0A]">${tier.deposit + (damageProtection ? 19 : 0) + addonsSubtotal} today</span> and securely save your card. On delivery day we automatically charge the remaining <span className="font-semibold text-[#0A0A0A]">${tier.price - tier.deposit}</span>.
                  </p>
                </div>
              </div>
            </aside>

            {/* RIGHT — calendar / form */}
            <div className="lg:col-span-8">
              {/* Stepper */}
              <div className="flex items-center gap-6 mb-10">
                <Step n={1} active={step === 1} done={step > 1} label="Pick your 2 weeks" />
                <span className="flex-1 h-px bg-neutral-300" />
                <Step n={2} active={step === 2} done={false} label="Your details" />
              </div>

              {step === 1 && (
                <div className="bg-white border border-neutral-200 p-6 md:p-10" data-testid="calendar-step">
                  <h3 className="font-display text-2xl md:text-3xl font-bold">
                    Choose your start date
                  </h3>
                  <p className="text-neutral-600 mt-2 max-w-md">
                    Click any available day to lock in your 14-day window.
                    Delivery happens on day one, pickup on day fourteen.
                  </p>

                  <div className="mt-8 inline-block border border-neutral-200 p-2 bg-white">
                    <DayPicker
                      mode="single"
                      selected={startDate}
                      onSelect={(d) => d && setStartDate(d)}
                      modifiers={calendarModifiers}
                      modifiersStyles={CAL_MODIFIER_STYLES}
                      disabled={(d) => isBefore(d, minDate)}
                      showOutsideDays
                      numberOfMonths={2}
                      classNames={CAL_CLASSNAMES}
                    />
                  </div>

                  {startDate && (
                    <div
                      className={`mt-8 flex flex-col sm:flex-row gap-4 sm:items-center justify-between border p-5 ${
                        availability && !availability.available
                          ? "bg-red-50 border-red-200"
                          : "bg-[#FF4F00]/5 border-[#FF4F00]/30"
                      }`}
                    >
                      <div>
                        <p className="text-xs tracking-[0.2em] uppercase text-neutral-600 font-semibold">Your window</p>
                        <p className="font-display text-xl font-bold mt-1" data-testid="window-display">
                          {format(startDate, "MMM d")} → {format(endDate, "MMM d, yyyy")}
                        </p>
                        {availability && (
                          <p
                            className={`text-xs mt-2 font-semibold tracking-wide ${
                              availability.available ? "text-neutral-600" : "text-red-700"
                            }`}
                            data-testid="availability-status"
                          >
                            {availability.available
                              ? `${availability.remaining} of ${availability.total} kits available for these dates`
                              : "Sold out for these dates — please pick a different start date"}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        data-testid="continue-to-details"
                        onClick={() => setStep(2)}
                        disabled={!availability || !availability.available}
                        className="bg-[#FF4F00] text-white px-7 py-4 font-semibold tracking-wide hover:bg-[#E64700] transition-colors inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#FF4F00]"
                      >
                        Continue <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <form
                  onSubmit={onSubmit}
                  className="bg-white border border-neutral-200 p-6 md:p-10"
                  data-testid="details-step"
                >
                  <h3 className="font-display text-2xl md:text-3xl font-bold">Your details</h3>
                  <p className="text-neutral-600 mt-2">We&apos;ll confirm by email within 1 business hour.</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
                    <Field
                      label="Full name"
                      testid="input-full-name"
                      value={form.full_name}
                      onChange={(v) => setForm({ ...form, full_name: v })}
                      required
                    />
                    <Field
                      label="Email"
                      type="email"
                      testid="input-email"
                      value={form.email}
                      onChange={(v) => setForm({ ...form, email: v })}
                      required
                    />
                    <Field
                      label="Phone"
                      testid="input-phone"
                      value={form.phone}
                      onChange={(v) => setForm({ ...form, phone: v })}
                      required
                    />
                    <Field
                      label="Delivery address"
                      testid="input-address"
                      value={form.delivery_address}
                      onChange={(v) => setForm({ ...form, delivery_address: v })}
                      required
                    />
                    <div className="md:col-span-2">
                      <label className="text-xs tracking-[0.2em] uppercase text-neutral-500 font-semibold">
                        Notes (optional)
                      </label>
                      <textarea
                        data-testid="input-notes"
                        rows={3}
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="mt-2 w-full border border-neutral-200 px-4 py-3 bg-white focus:outline-none focus:border-[#FF4F00] focus:ring-1 focus:ring-[#FF4F00] resize-none"
                        placeholder="Gate code, parking notes, etc."
                      />
                    </div>
                  </div>

                  {/* Damage protection opt-out */}
                  <label
                    className={`mt-8 flex items-start gap-4 border p-5 cursor-pointer transition-colors ${
                      damageProtection ? "bg-[#FF4F00]/5 border-[#FF4F00]/40" : "bg-white border-neutral-200"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={damageProtection}
                      onChange={(e) => setDamageProtection(e.target.checked)}
                      data-testid="damage-protection-toggle"
                      className="mt-1 w-4 h-4 accent-[#FF4F00] cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <p className="font-display text-base font-bold">Damage protection · +$19</p>
                        <span className="text-xs tracking-[0.2em] uppercase text-neutral-500 font-semibold">
                          Recommended
                        </span>
                      </div>
                      <p className="text-sm text-neutral-600 mt-1 leading-relaxed">
                        Covers minor wear-and-tear like a broken lid, a dolly wheel or a strap.
                        <span className="block text-xs text-neutral-500 mt-1">
                          Doesn&apos;t waive fees for cracked/missing totes ($35 each) or dolly replacement ($250).
                          Uncheck to skip.
                        </span>
                      </p>
                    </div>
                  </label>

                  {/* Add-ons picker */}
                  {addonCatalog.length > 0 && (
                    <div className="mt-8">
                      <div className="flex items-baseline justify-between mb-4">
                        <div>
                          <p className="text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold">Extras</p>
                          <h4 className="font-display text-xl font-bold mt-1">Need a little more?</h4>
                        </div>
                        {addonsSubtotal > 0 && (
                          <span className="text-sm font-semibold text-[#FF4F00]" data-testid="addons-subtotal">
                            + ${addonsSubtotal} added
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {addonCatalog.map((a) => (
                          <AddonRow
                            key={a.id}
                            addon={a}
                            qty={addonQty[a.id] || 0}
                            onChange={(n) => setAddonQty((q) => ({ ...q, [a.id]: Math.max(0, n) }))}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-10 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      data-testid="back-to-calendar"
                      className="inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-[#FF4F00]"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="confirm-booking"
                      className="bg-[#FF4F00] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#E64700] transition-colors inline-flex items-center gap-3 disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to secure checkout…
                        </>
                      ) : (
                        <>
                          Pay ${tier.deposit + (damageProtection ? 19 : 0) + addonsSubtotal} & save card <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between text-sm">
    <span className="text-neutral-500">{label}</span>
    <span className="font-semibold text-[#0A0A0A]" data-testid={testid}>{value}</span>
  </div>
);

const Step = ({ n, active, done, label }) => (
  <div className="flex items-center gap-3">
    <span
      className={`w-9 h-9 inline-flex items-center justify-center font-bold text-sm ${
        active
          ? "bg-[#FF4F00] text-white"
          : done
          ? "bg-[#0A0A0A] text-white"
          : "bg-white border border-neutral-300 text-neutral-500"
      }`}
    >
      {done ? <Check className="w-4 h-4" /> : n}
    </span>
    <span className={`text-sm font-semibold ${active ? "text-[#0A0A0A]" : "text-neutral-500"}`}>{label}</span>
  </div>
);

const Field = ({ label, value, onChange, required, type = "text", testid }) => (
  <div>
    <label className="text-xs tracking-[0.2em] uppercase text-neutral-500 font-semibold">{label}</label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-2 w-full border border-neutral-200 px-4 py-3 bg-white focus:outline-none focus:border-[#FF4F00] focus:ring-1 focus:ring-[#FF4F00]"
    />
  </div>
);

const ADDON_ICONS = {
  totes_10: Package,
  dolly: Truck,
  wardrobe: Shirt,
  extension_3: CalendarClock,
  extension_7: CalendarPlus,
};

const AddonRow = ({ addon, qty, onChange }) => {
  const Icon = ADDON_ICONS[addon.id] || Package;
  const active = qty > 0;
  return (
    <div
      data-testid={`booking-addon-${addon.id}`}
      className={`flex items-center gap-3 border p-3 ${active ? "border-[#FF4F00] bg-[#FF4F00]/5" : "border-neutral-200 bg-white"}`}
    >
      <div className="w-9 h-9 bg-[#FF4F00]/10 text-[#FF4F00] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{addon.name}</p>
        <p className="text-xs text-neutral-500">${addon.price} · per {addon.unit}</p>
      </div>
      <div className="inline-flex items-center border border-neutral-300 shrink-0">
        <button
          type="button"
          onClick={() => onChange(qty - 1)}
          disabled={qty === 0}
          data-testid={`booking-addon-decrement-${addon.id}`}
          className="w-7 h-7 text-sm hover:bg-neutral-100 disabled:opacity-30"
        >−</button>
        <span className="w-7 text-center text-sm font-bold" data-testid={`booking-addon-qty-${addon.id}`}>{qty}</span>
        <button
          type="button"
          onClick={() => onChange(qty + 1)}
          data-testid={`booking-addon-increment-${addon.id}`}
          className="w-7 h-7 text-sm hover:bg-neutral-100"
        >+</button>
      </div>
    </div>
  );
};
